/**
 * Pre-flight checks run before every copy.
 *
 * These are not style preferences. Each one corresponds to a real failure observed in the
 * wild, and most of them fail *silently* in Webflow — the rule vanishes, or the paste reports
 * "clipboard is empty", or the Designer crashes outright. Blocking the copy is kinder than
 * letting a marketer discover it mid-paste.
 */

import { SHORTHAND_DENYLIST } from '@lp/tokens';
import { isTextNode, type XscpDocument, type XscpElementNode } from './types.js';

export interface Violation {
  id: string;
  message: string;
}

/** Conservative. Odyn's converter API rejects >500KB; no Webflow-imposed limit is documented. */
export const MAX_PAYLOAD_BYTES = 400_000;

export function checkInvariants(doc: XscpDocument, serialised?: string): Violation[] {
  const v: Violation[] = [];
  const { nodes, styles } = doc.payload;

  const nodeIds = new Set(nodes.map((n) => n._id));
  const styleIds = new Set(styles.map((s) => s._id));

  if (nodeIds.size !== nodes.length) {
    v.push({ id: 'duplicate-node-id', message: 'Two nodes share an _id. Ids must be unique within a payload.' });
  }
  if (styleIds.size !== styles.length) {
    v.push({ id: 'duplicate-style-id', message: 'Two styles share an _id.' });
  }
  if (nodes.length === 0) {
    v.push({ id: 'empty-payload', message: 'Nothing to copy — this section produced no elements.' });
  }

  for (const node of nodes) {
    if (isTextNode(node)) continue;
    const el = node as XscpElementNode;

    for (const childId of el.children) {
      if (!nodeIds.has(childId)) {
        v.push({
          id: 'dangling-child',
          message: `Node ${el._id} (${el.type}) references a child ${childId} that is not in the payload.`,
        });
      }
    }

    for (const classId of el.classes) {
      if (!styleIds.has(classId)) {
        v.push({
          id: 'orphan-class',
          message: `Node ${el._id} (${el.type}) references a class that has no style definition.`,
        });
      }
    }

    if (el.type === 'Image') {
      const src = el.data?.attr?.['src'];
      // This one is not cosmetic: an <img> with an empty src crashes the Webflow Designer.
      if (!src) {
        v.push({
          id: 'image-empty-src',
          message: 'An image has no source. Pasting an empty <img> crashes the Webflow Designer.',
        });
      } else if (src.startsWith('data:')) {
        // An uploaded image lives as a data URL until there is somewhere to host it. Inlining
        // it would put the whole file in the clipboard payload — megabytes of base64 that
        // blows the size ceiling and leaves Webflow with an unmanageable asset.
        v.push({
          id: 'image-data-url',
          message:
            'An image is still an uploaded file rather than a hosted URL. Upload it to ' +
            'Webflow’s Assets panel and paste the link, then copy this section again.',
        });
      }
      // `"auto"` is what Webflow itself writes, and it is exactly as useless as a missing
      // value for reserving space — the browser still cannot size the box before the bytes
      // arrive. Our images always know their intrinsic dimensions, so require real numbers.
      const { width, height } = el.data?.attr ?? {};
      const usable = (d: string | undefined): boolean => !!d && d !== 'auto' && /^\d+$/.test(d);
      if (!usable(width) || !usable(height)) {
        v.push({
          id: 'image-no-dimensions',
          message:
            'An image has no usable width/height (got ' +
            `${width ?? 'nothing'}×${height ?? 'nothing'}). Without real pixel dimensions the ` +
            'browser cannot reserve space, which shows up as cumulative layout shift.',
        });
      }
    }
  }

  for (const style of styles) {
    const blobs = [style.styleLess, ...Object.values(style.variants).map((x) => x.styleLess)];
    for (const blob of blobs) {
      for (const prop of SHORTHAND_DENYLIST) {
        if (new RegExp(`(^|[;{]\\s*)${prop}\\s*:`).test(blob)) {
          v.push({
            id: 'shorthand-css',
            message: `Class "${style.name}" uses the shorthand "${prop}". Webflow drops shorthands from styleLess, so the rule would silently vanish.`,
          });
        }
      }
    }

    if (style.comb === '&') {
      const parents = styles.filter((s) => s.children.includes(style._id));
      if (parents.length !== 1) {
        v.push({
          id: 'combo-wiring',
          message: `Combo class "${style.name}" is listed by ${parents.length} base classes; it must be listed by exactly one.`,
        });
      }
    }
  }

  const json = serialised ?? JSON.stringify(doc);

  // Webflow's paste handler requires the clipboard to start exactly with `{`, and reports a
  // misleading "clipboard is empty" when it does not.
  if (json[0] !== '{') {
    v.push({ id: 'malformed-envelope', message: 'Payload does not start with "{".' });
  }
  if (doc.type !== '@webflow/XscpData') {
    v.push({ id: 'wrong-type', message: `Envelope type is "${doc.type}".` });
  }
  if (json.length > MAX_PAYLOAD_BYTES) {
    v.push({
      id: 'payload-too-large',
      message: `Payload is ${(json.length / 1024).toFixed(0)} KB. Copy one section at a time rather than the whole page.`,
    });
  }

  return v;
}
